/**
 * Toast 通知组件 — 全局轻量级消息提示系统。
 *
 * 主要导出：
 * - `ToastContainer`（自定义元素 `<toast-container>`）：Toast 容器组件
 * - `toast` 函数及 `.success/.error/.warning/.info/.block` 快捷方法
 * - `ToastType` 类型
 *
 * 设计决策：
 * - 使用单例模式：全局仅维护一个 `<toast-container>` 实例
 * - 容器固定在右上角，支持多条 Toast 堆叠
 * - 自动消失（默认 3500ms），支持手动关闭
 * - 支持退出动画（dismiss 时先标记 dismissing，250ms 后移除 DOM）
 *
 * 用法：
 *   toast('操作成功')                              // 默认 info 类型
 *   toast.success('保存成功')                       // 绿色成功提示
 *   toast.error('操作失败，请重试')                  // 红色错误提示
 *   toast.warning('请注意，数据可能过期')             // 黄色警告提示
 *   toast.info('邮件已加入发送队列')                 // 蓝色信息提示
 *   toast.block('账号已被封禁')                     // 红色阻断提示
 *   toast.show('自定义', 'success', 5000)           // 自定义类型和持续时间(毫秒)
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';

/** Toast 消息类型：success 成功 / error 错误 / info 信息 / warning 警告 / block 阻断 */
export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'block';

/** Toast 数据结构 */
interface Toast {
  /** 唯一标识（自增 ID） */
  id: number;
  /** 消息内容 */
  message: string;
  /** 消息类型 */
  type: ToastType;
  /** 自动消失时间（毫秒） */
  duration: number;
}

/** Toast ID 自增计数器 */
let nextId = 0;

@customElement('toast-container')
export class ToastContainer extends BaseElement {
  /** 当前显示的 Toast 列表 */
  @state()
  private toasts: Toast[] = [];

  /** 正在退出动画中的 Toast ID 列表（用于触发退出动画） */
  @state()
  private dismissingIds: number[] = [];

  static styles = css`
    /* 容器固定在右上角，不接收指针事件（但子元素可交互） */
    :host { position: fixed; top: 1rem; right: 1rem; z-index: 9999; display: flex; flex-direction: column; gap: 0.5rem; pointer-events: none; }
  `;

  /**
   * 显示一条 Toast 通知。
   * @param message - 消息内容
   * @param type - 消息类型，默认 info
   * @param duration - 自动消失时间（毫秒），默认 3500ms
   */
  show(message: string, type: ToastType = 'info', duration = 3500) {
    const id = nextId++;
    this.toasts = [...this.toasts, { id, message, type, duration }];
    // 到时间后自动触发退出动画
    setTimeout(() => this.dismiss(id), duration);
  }

  /**
   * 关闭指定 Toast — 先标记为退出中状态（触发退出动画），250ms 后从列表移除。
   * @param id - 要关闭的 Toast ID
   */
  dismiss(id: number) {
    // 防止重复 dismiss
    if (this.dismissingIds.includes(id)) return;
    this.dismissingIds = [...this.dismissingIds, id];
    // 等待退出动画完成后从 DOM 移除
    setTimeout(() => {
      this.toasts = this.toasts.filter(t => t.id !== id);
      this.dismissingIds = this.dismissingIds.filter(d => d !== id);
    }, 250);
  }

  /**
   * 根据 Toast 类型返回对应的 SVG 图标。
   * @param type - Toast 类型
   * @returns Lit 模板（SVG 图标）
   */
  private icon(type: ToastType): ReturnType<typeof html> {
    switch (type) {
      case 'success':
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 text-green-500 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`;
      case 'error':
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 text-red-500 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="15" x2="9" y1="9" y2="15"/><line x1="9" x2="15" y1="9" y2="15"/></svg>`;
      case 'warning':
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 text-yellow-500 flex-shrink-0"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.73-2Z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/></svg>`;
      case 'block':
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 text-red-500 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>`;
      default:
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 text-indigo-500 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>`;
    }
  }

  /**
   * 根据 Toast 类型返回对应的边框和背景颜色类名。
   * @param type - Toast 类型
   * @returns Tailwind CSS 类名字符串
   */
  private color(type: ToastType): string {
    switch (type) {
      case 'success': return 'border-green-200 bg-white';
      case 'error': return 'border-red-200 bg-white';
      case 'warning': return 'border-yellow-200 bg-white';
      case 'block': return 'border-red-300 bg-red-50';
      default: return 'border-indigo-200 bg-white';
    }
  }

  /**
   * 渲染所有 Toast 通知项。
   * 退出中的项使用 `animate-slide-out`，新出现的项使用 `animate-slide-in`。
   * @returns Lit 模板
   */
  render() {
    return html`
      ${this.toasts.map(t => html`
        <div class="pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-md border shadow-lg min-w-[280px] max-w-[400px] ${this.dismissingIds.includes(t.id) ? 'animate-slide-out' : 'animate-slide-in'} ${this.color(t.type)}">
          ${this.icon(t.type)}
          <span class="text-sm text-gray-800 flex-1 break-all">${t.message}</span>
          <button class="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0" @click=${() => this.dismiss(t.id)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      `)}
    `;
  }
}

/** 全局唯一的 Toast 容器实例（单例） */
let toastContainer: ToastContainer | null = null;

/**
 * 获取或创建全局 Toast 容器单例。
 * 先尝试从 DOM 中查找已存在的 `<toast-container>`，找不到则创建新实例并挂载到 body。
 * @returns ToastContainer 实例
 */
function getContainer(): ToastContainer {
  if (!toastContainer) {
    toastContainer = document.querySelector('toast-container') as ToastContainer | null;
    if (!toastContainer) {
      const el = document.createElement('toast-container') as ToastContainer;
      document.body.appendChild(el);
      toastContainer = el;
    }
  }
  return toastContainer;
}

/**
 * 公共 Toast 通知工具
 *
 * 用法:
 *   toast('操作成功')                              // 默认 info 类型
 *   toast.success('保存成功')                       // 绿色成功提示
 *   toast.error('操作失败，请重试')                  // 红色错误提示
 *   toast.warning('请注意，数据可能过期')             // 黄色警告提示
 *   toast.info('邮件已加入发送队列')                 // 蓝色信息提示
 *   toast.show('自定义', 'success', 5000)           // 自定义类型和持续时间(毫秒)
 *
 * @param message - 消息内容
 * @param type - 消息类型，默认 info
 * @param duration - 自动消失时间（毫秒），默认 3500ms
 */
export function toast(message: string, type: ToastType = 'info', duration = 3500) {
  getContainer().show(message, type, duration);
}

/** 快捷方法：显示成功提示（绿色） */
toast.success = (message: string, duration = 3500) => toast(message, 'success', duration);
/** 快捷方法：显示错误提示（红色） */
toast.error = (message: string, duration = 3500) => toast(message, 'error', duration);
/** 快捷方法：显示警告提示（黄色） */
toast.warning = (message: string, duration = 3500) => toast(message, 'warning', duration);
/** 快捷方法：显示信息提示（蓝色） */
toast.info = (message: string, duration = 3500) => toast(message, 'info', duration);
/** 快捷方法：显示阻断提示（红色背景，用于严重错误如账号封禁） */
toast.block = (message: string, duration = 3500) => toast(message, 'block', duration);
